import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../lib/api';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';
import Sidebar from '../components/Sidebar';

// ── Types ────────────────────────────────────────────────────────────────────

interface ProjectRow {
  id:   string;
  name: string;
}

interface RoleSlot {
  role:     string;
  status:   string;
  boundTo?: { userId: string; name: string; email: string };
}

interface TenantMemberRow {
  userId:    string;
  role:      string;
  userName:  string | null;
  userEmail: string | null;
}

// One row per (project, currently-BOUND role) — the unit both selection and
// incoming-role-assignment operate on, matching CT.A4's own per-participant-
// role design (custody-transfer.service.ts) rather than a per-person grouping.
//
// #2669 — role is null for a roleless row: a cohort_team_members-reachable
// student with no project_members role on this project at all. That row is
// revoke-only — there's no role to hand to an incoming person, so it's one
// row per PERSON rather than per role, and never requires an incoming
// selection (see allAssigned below).
interface OutgoingRow {
  projectId:   string;
  projectName: string;
  role:        string | null;
  userId:      string;
  name:        string;
  email:       string;
  selected:    boolean;
}

interface RolelessCohortMember {
  userId: string;
  name:   string | null;
  email:  string | null;
}

interface IncomingChoice {
  userId:    string;
  email:     string;
  name:      string;
  // #2726 — which project this specific choice was made for; carried through
  // to the submitted incomingGroup entry (see handleConfirm) so the backend
  // can scope the grant to that project instead of applying it everywhere.
  projectId: string;
}

interface PhaseBoundaryWarning {
  projectId: string;
  reason:    string;
  detail:    string;
}

interface Participant {
  userId: string;
  email:  string;
  role:   string | null;
}

// #2726 — an incoming grant is scoped to the one project its row belongs to;
// unlike outgoingGroup (deduped to one entry per PERSON across every named
// project, see handleConfirm), a single role name can be intended for
// different people on different projects within the same multi-project
// transfer, so incomingGroup entries must each name their own project.
interface IncomingParticipant extends Participant {
  projectId: string;
}

interface ExecuteResult {
  transfer: { id: string; status: string };
}

interface HistoryEntry {
  id:            string;
  outgoingGroup: Participant[];
  incomingGroup: Participant[];
  status:        string;
  createdAt:     string;
  completedAt:   string | null;
  projectIds:    string[];
}

type WizardStep = 'select' | 'review' | 'confirm' | 'done';

// ── Styles (page-local, matching every other Settings page's convention) ────

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)',
  padding: 24, marginBottom: 16,
};
const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
  color: 'var(--text)', display: 'block', marginBottom: 6,
};
const bodyText: React.CSSProperties = {
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px',
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', outline: 'none',
};
const btnPrimary: React.CSSProperties = {
  padding: '8px 16px', fontFamily: 'var(--font-display)', fontSize: 13,
  fontWeight: 600, color: 'white', background: 'var(--primary)',
  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '8px 16px', fontFamily: 'var(--font-display)', fontSize: 13,
  fontWeight: 600, color: 'var(--text)', background: 'var(--surface)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
};
const warnBox: React.CSSProperties = {
  background: 'var(--warning-bg, #fff8e1)', border: '1px solid var(--warning, #f0b429)',
  borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 12,
  fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)',
};

function rowKey(row: { projectId: string; role: string | null; userId: string }): string {
  // 'NONE' sentinel is safe — real role names come from ROLE_ORDER
  // (ARCHITECT/DEVELOPER/...) and never collide with it.
  return `${row.projectId}:${row.role ?? 'NONE'}:${row.userId}`;
}

export default function CustodyTransferPage() {
  const navigate = useNavigate();
  const { myTenant, loading: tenantLoading } = useMyTenant();
  const isAdmin  = isAdminRole(myTenant?.role);
  const tenantId = myTenant?.tenantId;

  // Fail-closed redirect for non-admins — mirrors SettingsStoragePage's own
  // pattern exactly. Server-side requireRole('TENANT_ADMIN') is the real
  // gate; this is UX, not the security boundary.
  useEffect(() => {
    if (!tenantLoading && !isAdmin) navigate('/settings');
  }, [tenantLoading, isAdmin, navigate]);

  const [view, setView] = useState<'wizard' | 'history'>('wizard');
  const [step, setStep] = useState<WizardStep>('select');

  const [projects, setProjects]                 = useState<ProjectRow[]>([]);
  const [projectsLoading, setProjectsLoading]   = useState(true);
  const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);

  const [outgoingRows, setOutgoingRows]         = useState<OutgoingRow[]>([]);
  const [tenantMembers, setTenantMembers]       = useState<TenantMemberRow[]>([]);
  const [incomingByRow, setIncomingByRow]       = useState<Record<string, IncomingChoice>>({});
  const [reviewLoading, setReviewLoading]       = useState(false);
  const [reviewError, setReviewError]           = useState<string | null>(null);

  const [warnings, setWarnings]                 = useState<PhaseBoundaryWarning[]>([]);
  const [warningsLoading, setWarningsLoading]   = useState(false);
  const [overrideReason, setOverrideReason]     = useState('');
  const [submitting, setSubmitting]             = useState(false);
  const [submitError, setSubmitError]           = useState<string | null>(null);
  const [result, setResult]                     = useState<ExecuteResult | null>(null);

  const [history, setHistory]                   = useState<HistoryEntry[] | null>(null);
  const [historyLoading, setHistoryLoading]     = useState(false);
  const [historyError, setHistoryError]         = useState<string | null>(null);
  const [projectsError, setProjectsError]       = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId || !isAdmin) return;
    let cancelled = false;
    apiFetch<ProjectRow[]>('/projects')
      .then((rows) => { if (!cancelled) setProjects(rows); })
      .catch((err) => { if (!cancelled) setProjectsError(err instanceof ApiError ? err.message : 'Could not load projects.'); })
      .finally(() => { if (!cancelled) setProjectsLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, isAdmin]);

  // R1 finding (ct-2535): a failed fetch here must NEVER be indistinguishable
  // from "no transfers exist yet" — this is an audit/compliance view, and a
  // silent empty-state on a transient error would misrepresent history as
  // clean when it's actually unknown.
  const loadHistory = useCallback(async () => {
    if (!tenantId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const rows = await apiFetch<HistoryEntry[]>(`/tenants/${tenantId}/custody-transfers`);
      setHistory(rows);
    } catch (err) {
      setHistoryError(err instanceof ApiError ? err.message : 'Could not load custody transfer history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    if (view === 'history' && tenantId) void loadHistory();
  }, [view, tenantId, loadHistory]);

  function toggleProject(id: string) {
    setSelectedProjectIds((prev) => prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]);
  }

  async function proceedToReview() {
    if (!tenantId || selectedProjectIds.length === 0) return;
    setReviewLoading(true);
    setReviewError(null);
    try {
      const [slotsByProject, rolelessByProject, members] = await Promise.all([
        Promise.all(selectedProjectIds.map((pid) =>
          apiFetch<RoleSlot[]>(`/projects/${pid}/roles`).then((slots) => ({ pid, slots })),
        )),
        // #2669 — the other half of Group Review's outgoing side: students
        // reachable via cohort_team_members with no project_members role at
        // all, invisible to /projects/:projectId/roles by construction.
        Promise.all(selectedProjectIds.map((pid) =>
          apiFetch<RolelessCohortMember[]>(`/tenants/${tenantId}/custody-transfers/roleless-members/${pid}`)
            .then((rolelessMembers) => ({ pid, rolelessMembers })),
        )),
        apiFetch<TenantMemberRow[]>(`/tenants/${tenantId}/members`),
      ]);

      const rows: OutgoingRow[] = [];
      for (const { pid, slots } of slotsByProject) {
        const projectName = projects.find((p) => p.id === pid)?.name ?? pid;
        for (const slot of slots) {
          if (slot.status === 'BOUND' && slot.boundTo) {
            rows.push({
              projectId: pid, projectName, role: slot.role,
              userId: slot.boundTo.userId, name: slot.boundTo.name, email: slot.boundTo.email,
              selected: false,
            });
          }
        }
      }
      for (const { pid, rolelessMembers } of rolelessByProject) {
        const projectName = projects.find((p) => p.id === pid)?.name ?? pid;
        for (const m of rolelessMembers) {
          rows.push({
            projectId: pid, projectName, role: null,
            userId: m.userId, name: m.name ?? m.email ?? m.userId, email: m.email ?? '',
            selected: false,
          });
        }
      }
      setOutgoingRows(rows);
      setTenantMembers(members);
      setIncomingByRow({});
      setStep('review');
    } catch (err) {
      setReviewError(err instanceof ApiError ? err.message : 'Could not load project roles.');
    } finally {
      setReviewLoading(false);
    }
  }

  function toggleRow(row: OutgoingRow) {
    setOutgoingRows((prev) => prev.map((r) => r === row ? { ...r, selected: !r.selected } : r));
    if (row.selected) {
      setIncomingByRow((prev) => { const next = { ...prev }; delete next[rowKey(row)]; return next; });
    }
  }

  // Sets the explicit incoming choice for one row. Also SUGGESTS the same
  // person for any other still-unassigned, currently-selected row for the
  // SAME outgoing user on the SAME project — REC's real pattern (one person
  // holding all five CREATOR_ROLES, replaced by one incoming person) as the
  // natural default — but never overwrites a row the initiator already set
  // explicitly (§3: initiator must be able to see and change it per person).
  function setIncomingForRow(row: OutgoingRow, member: TenantMemberRow) {
    const choice: IncomingChoice = { userId: member.userId, email: member.userEmail ?? '', name: member.userName ?? member.userEmail ?? member.userId, projectId: row.projectId };
    setIncomingByRow((prev) => {
      const next = { ...prev, [rowKey(row)]: choice };
      for (const r of outgoingRows) {
        if (r.selected && r.projectId === row.projectId && r.userId === row.userId && !(rowKey(r) in prev)) {
          next[rowKey(r)] = choice;
        }
      }
      return next;
    });
  }

  const selectedRows = outgoingRows.filter((r) => r.selected);
  // #2669 — a roleless row is revoke-only: there's no role to hand to an
  // incoming person, so it never needs (or shows) an incoming assignment.
  const allAssigned  = selectedRows.length > 0 && selectedRows.every((r) => r.role === null || incomingByRow[rowKey(r)]);

  async function proceedToConfirm() {
    if (!tenantId) return;
    setWarningsLoading(true);
    setReviewError(null);
    // #2726 — without this, a stale submitError from a PREVIOUS failed
    // Confirm attempt (e.g. Back → fix the offending row → Next: Confirm
    // again) would still render on the Confirmation screen even though the
    // underlying selection is now materially different. reviewError above
    // already resets on every attempt; submitError only ever reset inside
    // handleConfirm itself, which is too late — it needs to be gone before
    // the Confirmation screen even re-renders.
    setSubmitError(null);
    try {
      const res = await apiFetch<{ warnings: PhaseBoundaryWarning[] }>(
        `/tenants/${tenantId}/custody-transfers/preview`,
        { method: 'POST', body: JSON.stringify({ projectIds: selectedProjectIds }) },
      );
      setWarnings(res.warnings);
      setOverrideReason('');
      setStep('confirm');
    } catch (err) {
      // R1 finding (ct-2535): without this, a failed preview call left the
      // Group Review screen looking unchanged with Next re-enabled — clicking
      // it appeared to silently do nothing, with no indication why.
      setReviewError(err instanceof ApiError ? err.message : 'Could not check this transfer for phase-boundary warnings.');
    } finally {
      setWarningsLoading(false);
    }
  }

  const canConfirm = allAssigned && (warnings.length === 0 || overrideReason.trim().length > 0);

  async function handleConfirm() {
    if (!tenantId || !canConfirm) return;
    setSubmitting(true);
    setSubmitError(null);
    // Dedupe by userId, not per role — CT.A4's bulkProvisionCustodyTransferAccess
    // already removes EVERY role an outgoing participant holds on the named
    // projects in one pass (its own explicit design, ct-2534), and its dedup
    // guard rejects the same userId listed twice. Group Review's per-row model
    // (one row per role) means the SAME outgoing person appears in multiple
    // `selectedRows` whenever they hold multiple roles — REC's own common
    // pattern (one person holding all five CREATOR_ROLES) — so this MUST
    // collapse to one entry per distinct person before submitting, or the
    // request is rejected outright. Caught via manual browser verification,
    // not the pglite/unit suites (neither exercises the real per-row UI model
    // against the real backend's dedup guard together).
    const outgoingGroup: Participant[] = Array.from(
      new Map(selectedRows.map((r) => [r.userId, { userId: r.userId, email: r.email, role: r.role }])).values(),
    );
    // #2669 — a roleless row has no incoming counterpart (nothing to
    // reassign), so it's excluded here — it's still present in
    // outgoingGroup above, which is what actually drives its revocation.
    // #2726 — projectId: r.projectId (the row this selection is FOR), not
    // derived from `inc` — every entry names its own project explicitly so
    // the backend can scope the grant instead of applying it to every
    // selected project.
    const incomingGroup: IncomingParticipant[] = selectedRows
      .filter((r) => r.role !== null)
      .map((r) => {
        const inc = incomingByRow[rowKey(r)];
        return { userId: inc.userId, email: inc.email, role: r.role, projectId: r.projectId };
      });
    try {
      const res = await apiFetch<ExecuteResult>(`/tenants/${tenantId}/custody-transfers`, {
        method: 'POST',
        body: JSON.stringify({
          projectIds: selectedProjectIds,
          outgoingGroup,
          incomingGroup,
          ...(warnings.length > 0 ? { overrideReason: overrideReason.trim() } : {}),
        }),
      });
      setResult(res);
      setStep('done');
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Something went wrong completing this transfer.');
    } finally {
      setSubmitting(false);
    }
  }

  function startOver() {
    setStep('select');
    setSelectedProjectIds([]);
    setOutgoingRows([]);
    setIncomingByRow({});
    setWarnings([]);
    setOverrideReason('');
    setResult(null);
    setSubmitError(null);
  }

  if (tenantLoading || !isAdmin || !tenantId) {
    return (
      <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
        <Sidebar />
        <main className="flex-1 px-8 py-6">
          <p style={bodyText}>Loading...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--bg)' }}>
      <Sidebar />
      <main className="flex-1 px-8 py-6">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
            Custody Transfer
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={view === 'wizard' ? btnPrimary : btnGhost} onClick={() => setView('wizard')}>New Transfer</button>
            <button style={view === 'history' ? btnPrimary : btnGhost} onClick={() => setView('history')}>History</button>
          </div>
        </div>

        <div style={{ maxWidth: 820 }}>
          {view === 'history' ? (
            <div style={cardStyle}>
              {historyLoading ? (
                <p style={bodyText}>Loading history...</p>
              ) : historyError ? (
                // R1 finding (ct-2535): this branch MUST come before the
                // empty-state check below — collapsing "fetch failed" into
                // "no transfers yet" would misrepresent an unknown history
                // as a clean one, on a screen whose whole purpose is an
                // audit trail.
                <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)' }} data-testid="custody-transfer-history-error">{historyError}</p>
              ) : !history || history.length === 0 ? (
                <p style={bodyText}>No custody transfers yet.</p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="custody-transfer-history-table">
                  <thead>
                    <tr>
                      {['Date', 'Status', 'Projects', 'Outgoing', 'Incoming'].map((h) => (
                        <th key={h} style={{ textAlign: 'left', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', padding: '8px 4px' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((t) => (
                      <tr key={t.id}>
                        <td style={{ padding: '8px 4px', ...bodyText }}>{new Date(t.createdAt).toLocaleString()}</td>
                        <td style={{ padding: '8px 4px', ...bodyText }}>{t.status}</td>
                        <td style={{ padding: '8px 4px', ...bodyText }}>{t.projectIds.length}</td>
                        <td style={{ padding: '8px 4px', ...bodyText }}>{t.outgoingGroup.map((p) => `${p.email} (${p.role ?? 'no role'})`).join(', ')}</td>
                        <td style={{ padding: '8px 4px', ...bodyText }}>{t.incomingGroup.map((p) => `${p.email} (${p.role ?? 'no role'})`).join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ) : (
            <>
              {step === 'select' && (
                <div style={cardStyle}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>1. Select projects</h2>
                  <p style={{ ...bodyText, marginBottom: 16 }}>Choose which project(s) this custody transfer covers.</p>
                  {projectsError && <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)', marginBottom: 8 }} data-testid="custody-transfer-projects-error">{projectsError}</p>}
                  {projectsLoading ? (
                    <p style={bodyText}>Loading projects...</p>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      {projects.map((p) => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', ...bodyText }}>
                          <input
                            type="checkbox"
                            checked={selectedProjectIds.includes(p.id)}
                            onChange={() => toggleProject(p.id)}
                          />
                          {p.name}
                        </label>
                      ))}
                    </div>
                  )}
                  {reviewError && <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)', marginBottom: 8 }}>{reviewError}</p>}
                  <button
                    style={{ ...btnPrimary, opacity: selectedProjectIds.length === 0 || reviewLoading ? 0.6 : 1 }}
                    disabled={selectedProjectIds.length === 0 || reviewLoading}
                    onClick={() => void proceedToReview()}
                  >
                    {reviewLoading ? 'Loading…' : 'Next: Review Groups →'}
                  </button>
                </div>
              )}

              {step === 'review' && (
                <div style={cardStyle}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>2. Group Review</h2>
                  <p style={{ ...bodyText, marginBottom: 16 }}>
                    Select who is leaving each role, and choose who takes it over. Each role is assigned explicitly —
                    picking one person for a project&rsquo;s first role suggests them for the rest, but every row can be
                    changed independently.
                  </p>
                  {outgoingRows.length === 0 ? (
                    <p style={bodyText}>The selected project(s) have no bound role holders or cohort-team members to transfer.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }} data-testid="custody-transfer-review-table">
                      <thead>
                        <tr>
                          {['', 'Project', 'Role', 'Outgoing', 'Incoming'].map((h) => (
                            <th key={h} style={{ textAlign: 'left', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', padding: '8px 4px' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {outgoingRows.map((row) => {
                          const key = rowKey(row);
                          const incoming = incomingByRow[key];
                          return (
                            <tr key={key} data-testid="custody-transfer-review-row">
                              <td style={{ padding: '8px 4px' }}>
                                <input type="checkbox" checked={row.selected} onChange={() => toggleRow(row)} />
                              </td>
                              <td style={{ padding: '8px 4px', ...bodyText }}>{row.projectName}</td>
                              <td style={{ padding: '8px 4px', ...bodyText }}>{row.role ?? '— no role held'}</td>
                              <td style={{ padding: '8px 4px', ...bodyText }}>{row.name} ({row.email})</td>
                              <td style={{ padding: '8px 4px' }}>
                                {row.role === null ? (
                                  <span style={{ ...bodyText, color: 'var(--text-secondary)' }} data-testid="custody-transfer-revoke-only">
                                    Revoke access only — no role to reassign
                                  </span>
                                ) : row.selected ? (
                                  <select
                                    style={inputStyle}
                                    value={incoming?.userId ?? ''}
                                    data-testid="custody-transfer-incoming-select"
                                    onChange={(e) => {
                                      const member = tenantMembers.find((m) => m.userId === e.target.value);
                                      if (member) setIncomingForRow(row, member);
                                    }}
                                  >
                                    <option value="" disabled>Choose incoming person…</option>
                                    {tenantMembers.map((m) => (
                                      <option key={m.userId} value={m.userId}>{m.userName ?? m.userEmail} ({m.userEmail})</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span style={{ ...bodyText, color: 'var(--text-secondary)' }}>—</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  {selectedRows.length > 0 && !allAssigned && (
                    <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)', marginBottom: 8 }} data-testid="custody-transfer-incomplete-warning">
                      Every selected role (other than revoke-only rows) needs an incoming person before you can continue.
                    </p>
                  )}
                  {reviewError && <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)', marginBottom: 8 }} data-testid="custody-transfer-review-error">{reviewError}</p>}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btnGhost} onClick={() => setStep('select')}>← Back</button>
                    <button
                      style={{ ...btnPrimary, opacity: !allAssigned || warningsLoading ? 0.6 : 1 }}
                      disabled={!allAssigned || warningsLoading}
                      data-testid="custody-transfer-review-next"
                      onClick={() => void proceedToConfirm()}
                    >
                      {warningsLoading ? 'Checking…' : 'Next: Confirm →'}
                    </button>
                  </div>
                </div>
              )}

              {step === 'confirm' && (
                <div style={cardStyle}>
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>3. Confirmation</h2>
                  <p style={{ ...bodyText, marginBottom: 16 }}>Review the full transfer below before confirming.</p>

                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }} data-testid="custody-transfer-summary-table">
                    <thead>
                      <tr>
                        {['Project', 'Role', 'Outgoing', 'Incoming'].map((h) => (
                          <th key={h} style={{ textAlign: 'left', fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', padding: '8px 4px' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedRows.map((row) => {
                        const incoming = incomingByRow[rowKey(row)];
                        return (
                          <tr key={rowKey(row)}>
                            <td style={{ padding: '8px 4px', ...bodyText }}>{row.projectName}</td>
                            <td style={{ padding: '8px 4px', ...bodyText }}>{row.role ?? '— no role held'}</td>
                            <td style={{ padding: '8px 4px', ...bodyText }}>{row.name} ({row.email})</td>
                            <td style={{ padding: '8px 4px', ...bodyText }}>
                              {incoming ? `${incoming.name} (${incoming.email})` : 'Revoke access only'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {warnings.length > 0 && (
                    <div data-testid="custody-transfer-warnings">
                      {warnings.map((w, i) => (
                        <div key={i} style={warnBox}>
                          <strong>Phase-boundary warning:</strong> {w.detail}
                        </div>
                      ))}
                      <label style={labelStyle}>Reason for proceeding despite the warning above</label>
                      <textarea
                        style={{ ...inputStyle, minHeight: 72, marginBottom: 16 }}
                        value={overrideReason}
                        data-testid="custody-transfer-override-reason"
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="Explain why this transfer should proceed now"
                      />
                    </div>
                  )}

                  {submitError && <p style={{ ...bodyText, color: 'var(--danger, #d32f2f)', marginBottom: 8 }}>{submitError}</p>}

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btnGhost} onClick={() => setStep('review')}>← Back</button>
                    <button
                      style={{ ...btnPrimary, opacity: !canConfirm || submitting ? 0.6 : 1 }}
                      disabled={!canConfirm || submitting}
                      data-testid="custody-transfer-confirm-button"
                      onClick={() => void handleConfirm()}
                    >
                      {submitting ? 'Transferring…' : 'Confirm Transfer'}
                    </button>
                  </div>
                </div>
              )}

              {step === 'done' && result && (
                <div style={cardStyle} data-testid="custody-transfer-done">
                  <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>Transfer complete</h2>
                  <p style={{ ...bodyText, marginBottom: 16 }}>Status: {result.transfer.status}</p>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={btnPrimary} onClick={startOver}>Start Another Transfer</button>
                    <button style={btnGhost} onClick={() => setView('history')}>View History</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
